import { Controller } from "@hotwired/stimulus"

// Drives the live state of the Add Provider drawer.
//
// Selection state is managed client-side in this.selections (a Map).
// When a selection changes, the controller updates the summary counter
// and (optionally) POSTs to /selection to get a server-rendered Selected zone
// via Turbo Stream.
//
// The controller no longer builds/clones card DOM or embeds SVG.
// All rendering is delegated to ERB partials.
export default class extends Controller {
  static values = { selectionUrl: String }

  connect() {
    this.selections = new Map()
    this._selectionAbortController = null
    this._selectionRequestId = 0
    this._zoneRefreshPending = false
    this._syncFromCheckboxes()
    this._hideSelectedSearchResults()
    this._updateSummary()
    if (this.selections.size > 0) this._debouncedRefreshSelectedZone()
  }

  disconnect() {
    // Abort any pending selection requests
    if (this._selectionAbortController) this._selectionAbortController.abort()
    this.selections.clear()
  }

  // Handles clicks on the row area outside the checkbox visual so that
  // clicking the location name / icon / subtitle still toggles the checkbox.
  rowClicked(event) {
    if (event.target.closest('.gb-checkbox')) return
    if (event.target.closest('button, a, select, textarea, input')) return

    const row = event.currentTarget
    const checkbox = row.querySelector('input[type="checkbox"]:not(:disabled)')
    if (!checkbox) return

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Called when a checkbox in search results or the selected zone is
  // checked/unchecked.
  selectionChanged(event) {
    const checkbox = event.target
    const resultRow = checkbox.closest("[data-add-provider-result-type]")
    if (!resultRow) return

    const type = resultRow.dataset.addProviderResultType
    const id = resultRow.dataset.addProviderResultId
    const key = `${type}:${id}`
    const isInSelectedZone = !!resultRow.closest('#add_provider_selected_zone')

    if (checkbox.checked) {
      this.selections.set(key, {
        type,
        id,
        name: resultRow.dataset.addProviderResultName || "",
        subtitle: resultRow.dataset.addProviderResultSubtitle || ""
      })
      // Fade out the search result card (it will appear in the selected zone)
      if (!isInSelectedZone) this._fadeOutSearchResult(resultRow)
    } else {
      this.selections.delete(key)
      this._syncAllCardsForEntity(type, id, false)

      // Show the search result card again
      if (isInSelectedZone) this._fadeInSearchResult(type, id)
    }

    if (!isInSelectedZone) {
      this._toggleCardBorder(resultRow, checkbox.checked)
      this._toggleSettingsPanel(resultRow, checkbox.checked)
    }
    this._updateSummary()
    this._debouncedRefreshSelectedZone()
  }

  // Called after Turbo replaces search results (new search).
  refresh() {
    this._hideSelectedSearchResults()
    this._updateSummary()
  }

  // Called when an EIN chip on a location card is clicked — copies the EIN
  // to the search field and triggers a search.
  einChipClicked(event) {
    const ein = event.currentTarget.dataset.ein
    if (!ein) return
    const searchInput = document.getElementById("add_provider_q")
    if (searchInput) {
      searchInput.value = ein
      searchInput.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  // Remove a selection from the selected zone (× button).
  removeSelection(event) {
    const type = event.currentTarget.dataset.entityType
    const id = event.currentTarget.dataset.entityId
    const key = `${type}:${id}`
    this.selections.delete(key)

    this._syncAllCardsForEntity(type, id, false)
    this._updateSummary()

    // Disable form inputs before animating to prevent stale submissions
    const zoneRow = document.querySelector(
      `#add_provider_selected_zone [data-add-provider-result-type="${type}"][data-add-provider-result-id="${id}"]`
    )
    const zoneCard = zoneRow?.closest('.border.rounded-xl')
    this._disableFormInputs(zoneCard)

    this._animateZoneCardOut(type, id, () => {
      this._fadeInSearchResult(type, id)
      this._debouncedRefreshSelectedZone()
    })
  }

  // Toggle override on a system location
  toggleSystemLocationOverride(event) {
    // placeholder for future Turbo interaction
    const btn = event.currentTarget
    const systemId = btn.dataset.systemId
    const locationId = btn.dataset.locationId
    // This will POST to /selection to re-render the selected zone with the override toggled
    console.log(`Toggle override: system=${systemId} location=${locationId}`)
  }

  // Deselect a location from within a system card in the selected zone.
  removeSystemLocation(event) {
    const locationId = event.currentTarget.dataset.locationId
    const key = `location:${locationId}`
    this.selections.delete(key)

    this._syncAllCardsForEntity('location', locationId, false)
    this._updateSummary()

    // Find the wrapper row (includes the location row + optional override)
    const row = event.currentTarget.closest('[data-system-location-row]')
    this._disableFormInputs(row)

    this._animateElementOut(row, () => {
      this._fadeInSearchResult('location', locationId)
      this._debouncedRefreshSelectedZone()
    })
  }

  // Add a whole health-system selection (all locations).
  addSystemSelection(systemId, systemName) {
    const key = `system:${systemId}`
    this.selections.set(key, { type: 'system', id: systemId, name: systemName, subtitle: '' })
    this._toggleHsCardBorder(systemId, true)
    this._updateSummary()
    this._debouncedRefreshSelectedZone()
  }

  // Remove a system-level selection.
  removeSystemSelection(systemId) {
    const key = `system:${systemId}`
    this.selections.delete(key)
    // Uncheck the HS checkbox in the search results
    const hsCard = document.getElementById(`hs_drilldown_${systemId}`)
    if (hsCard) {
      const cb = hsCard.querySelector('[data-hs-select-all-target="hsCheckbox"]')
      if (cb) { cb.checked = false; cb.indeterminate = false }
    }
    this._toggleHsCardBorder(systemId, false)
    this._updateSummary()
    this._debouncedRefreshSelectedZone()
  }

  clearSelections() {
    this.selections.clear()

    // Uncheck any result checkboxes that are still in the DOM and show
    // any hidden search-result cards.
    document
      .querySelectorAll(
        'input[type=checkbox][form=add_provider_form]:not(:disabled)'
      )
      .forEach((cb) => {
        if (cb.checked) cb.checked = false
      })
    this._showAllSearchResults()

    // Clear HS "select all" checkboxes.
    document
      .querySelectorAll('input[type=checkbox][data-hs-select-all-target="hsCheckbox"]')
      .forEach((cb) => {
        cb.checked = false
        cb.indeterminate = false
      })

    // Hide all auto-reveal settings panels.
    document
      .querySelectorAll("[data-add-provider-counter-settings-panel]")
      .forEach((panel) => panel.classList.add("hidden"))

    // Clear the selected zone
    const zone = document.getElementById("add_provider_selected_zone")
    if (zone) zone.innerHTML = ""

    this._updateSummary()
    this._debouncedRefreshSelectedZone()
  }

  // ── Private ────────────────────────────────────────────────────────────

  // On connect, scan for any pre-checked checkboxes (e.g. auto_expand)
  // and seed our selection map from them.
  _syncFromCheckboxes() {
    document
      .querySelectorAll(
        'input[type=checkbox][form=add_provider_form]:checked:not(:disabled)'
      )
      .forEach((cb) => {
        const row = cb.closest("[data-add-provider-result-type]")
        if (!row) return
        const type = row.dataset.addProviderResultType
        const id = row.dataset.addProviderResultId
        const key = `${type}:${id}`
        if (!this.selections.has(key)) {
          this.selections.set(key, {
            type,
            id,
            name: row.dataset.addProviderResultName || "",
            subtitle: row.dataset.addProviderResultSubtitle || ""
          })
        }
      })
  }

  _toggleCardBorder(resultRow, checked) {
    const card = resultRow.closest('.border.rounded-xl')
    if (!card) return
    // Skip HS drilldown cards — their border is managed by tristate logic
    if (card.id && card.id.startsWith('hs_drilldown_')) return

    const selectedClasses = ['border-core-purple-500', 'bg-[#FCFCFF]', 'shadow-[0_0_0_1px_#4C56E0,0_6px_18px_rgba(76,86,224,0.10)]']
    const defaultClasses = ['border-support-light-300', 'hover:border-support-light-500', 'hover:shadow-[0_4px_16px_rgba(45,48,71,0.07)]']

    if (checked) {
      defaultClasses.forEach(c => card.classList.remove(c))
      selectedClasses.forEach(c => card.classList.add(c))
    } else {
      selectedClasses.forEach(c => card.classList.remove(c))
      defaultClasses.forEach(c => card.classList.add(c))
    }
  }

  _toggleHsCardBorder(systemId, selected) {
    const card = document.getElementById(`hs_drilldown_${systemId}`)
    if (!card) return
    const selectedClasses = ['border-core-purple-500', 'bg-[#FCFCFF]', 'shadow-[0_0_0_1px_#4C56E0,0_6px_18px_rgba(76,86,224,0.10)]']
    const defaultClasses = ['border-support-light-300', 'hover:border-support-light-500', 'hover:shadow-[0_4px_16px_rgba(45,48,71,0.07)]']
    if (selected) {
      defaultClasses.forEach(c => card.classList.remove(c))
      selectedClasses.forEach(c => card.classList.add(c))
    } else {
      selectedClasses.forEach(c => card.classList.remove(c))
      defaultClasses.forEach(c => card.classList.add(c))
    }
  }

  _refreshSelectedZone() {
    if (!this.hasSelectionUrlValue) return

    if (this._selectionAbortController) this._selectionAbortController.abort()
    this._selectionAbortController = new AbortController()
    const requestId = ++this._selectionRequestId

    // Snapshot zone state BEFORE the fetch for animation
    const zone = document.getElementById('add_provider_selected_zone')
    const oldHeight = zone ? zone.scrollHeight : 0
    const hadContent = zone && zone.children.length > 0
    const existingCardKeys = new Set()
    if (zone) {
      zone.querySelectorAll('[data-add-provider-result-type]').forEach(row => {
        existingCardKeys.add(`${row.dataset.addProviderResultType}:${row.dataset.addProviderResultId}`)
      })
    }

    const body = new FormData()
    this.selections.forEach(({ type, id }) => {
      if (type === 'location') {
        body.append(`locations[${id}][_selected]`, '1')
      } else if (type === 'ein') {
        body.append(`eins_selected[${id}][_selected]`, '1')
      } else if (type === 'system') {
        body.append(`systems[${id}][all]`, 'true')
      }
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.selectionUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': csrfToken
      },
      body,
      signal: this._selectionAbortController.signal
    })
      .then(response => response.text())
      .then(html => {
        if (requestId !== this._selectionRequestId) return

        // Lock zone height before Turbo replaces content to prevent layout jump
        if (zone && hadContent && !this._prefersReducedMotion()) {
          zone.style.height = oldHeight + 'px'
          this._applyZoneOverflow(zone)
        }

        if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
          Turbo.renderStreamMessage(html)
        }

        // Immediately hide new cards before browser paints (prevents flash)
        const freshZone = document.getElementById('add_provider_selected_zone')
        const newCards = []
        if (freshZone && !this._prefersReducedMotion()) {
          freshZone.querySelectorAll('[data-add-provider-result-type]').forEach(row => {
            const key = `${row.dataset.addProviderResultType}:${row.dataset.addProviderResultId}`
            if (!existingCardKeys.has(key)) {
              const card = row.closest('.border.rounded-xl')
              if (card) {
                card.style.opacity = '0'
                card.style.transform = 'translateY(-6px)'
                newCards.push(card)
              }
            }
          })
        }

        this._animateSelectedZone(oldHeight, hadContent, newCards, freshZone)
      })
      .catch(() => { /* silently ignore aborted/failed selection preview requests */ })
  }

  _findResultRow(type, id) {
    return document.querySelector(
      `[data-add-provider-result-type="${type}"][data-add-provider-result-id="${id}"]`
    )
  }

  // Find the search-result card (not the one in the selected zone).
  _findSearchResultRow(type, id) {
    const selector = `[data-add-provider-result-type="${type}"][data-add-provider-result-id="${id}"]`
    const all = document.querySelectorAll(selector)
    for (const row of all) {
      if (!row.closest('#add_provider_selected_zone')) return row
    }
    return null
  }

  // Uncheck/check every card matching this entity across both the selected
  // zone and the search results so they stay in sync.
  _syncAllCardsForEntity(type, id, checked) {
    document
      .querySelectorAll(
        `[data-add-provider-result-type="${type}"][data-add-provider-result-id="${id}"]`
      )
      .forEach((row) => {
        if (row.closest('#add_provider_selected_zone')) return
        const cb = row.querySelector('input[type="checkbox"]:not(:disabled)')
        if (cb && cb.checked !== checked) cb.checked = checked
        this._toggleCardBorder(row, checked)
        this._toggleSettingsPanel(row, checked)
      })
  }

  // ── Animation choreography ──────────────────────────────────────────
  //
  //  Selection:   result fade-out → zone expand → card fade-in → badge pop
  //  Deselection: zone shrink + card re-appears in results

  // Animate a search-result card out when it is selected.
  // Uses a single combined transition to avoid mid-animation snaps.
  _fadeOutSearchResult(row) {
    const card = row.closest('.border.rounded-xl')
    if (!card) return

    if (this._prefersReducedMotion()) {
      card.classList.add('hidden')
      return
    }

    const h = card.scrollHeight
    // Lock starting height so the transition has a concrete "from" value.
    card.style.overflow = 'hidden'
    card.style.maxHeight = h + 'px'
    card.style.transition = 'none'
    void card.offsetHeight // force reflow

    // Single combined transition: opacity fades immediately,
    // height + margin collapse with a slight delay to overlap smoothly.
    card.style.transition = 'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1), ' +
                            'max-height 280ms cubic-bezier(0.4, 0, 0.2, 1) 100ms, ' +
                            'margin-bottom 280ms cubic-bezier(0.4, 0, 0.2, 1) 100ms'
    card.style.opacity = '0'
    card.style.maxHeight = '0px'
    card.style.marginBottom = '0px'

    this._onTransitionEnd(card, 'max-height', 450, () => {
      card.classList.add('hidden')
      this._resetCardStyles(card)
      this._disableFormInputs(card)
    })
  }

  // Steps 1,4,6: Animate the selected zone after Turbo Stream replaces its content.
  // newCards and zone are pre-computed in _refreshSelectedZone (cards already hidden).
  _animateSelectedZone(oldHeight, hadContent, newCards, zone) {
    if (!zone) return
    const hasContent = zone.children.length > 0
    if (!hasContent) return

    if (this._prefersReducedMotion()) {
      this._cleanUpZoneStyles(zone)
      newCards.forEach(card => {
        card.style.removeProperty('opacity')
        card.style.removeProperty('transform')
      })
      return
    }

    // Temporarily remove height lock to measure natural height, then re-lock
    zone.style.removeProperty('height')
    const newHeight = zone.scrollHeight

    // Step 1 – Expand (or appear) the selected zone
    if (!hadContent) {
      // Zone is brand-new: slide open from zero
      this._applyZoneOverflow(zone)
      zone.style.height = '0px'
      zone.style.opacity = '0'
      zone.style.transition = 'none'
      requestAnimationFrame(() => {
        zone.style.transition = 'height 400ms cubic-bezier(0.25, 0.1, 0.25, 1), ' +
                                'opacity 300ms cubic-bezier(0.25, 0.1, 0.25, 1)'
        zone.style.height = newHeight + 'px'
        zone.style.opacity = '1'
      })
      setTimeout(() => this._cleanUpZoneStyles(zone), 450)
    } else if (newHeight !== oldHeight) {
      // Zone was already visible — animate from locked height to new height
      this._applyZoneOverflow(zone)
      zone.style.height = oldHeight + 'px'
      zone.style.transition = 'height 350ms cubic-bezier(0.25, 0.1, 0.25, 1)'
      requestAnimationFrame(() => {
        zone.style.height = newHeight + 'px'
      })
      setTimeout(() => this._cleanUpZoneStyles(zone), 400)
    } else {
      this._cleanUpZoneStyles(zone)
    }

    // Step 4 – Fade in new cards (staggered 80ms each, starts 120ms after zone expansion)
    newCards.forEach((card, i) => {
      const delay = 120 + i * 80
      setTimeout(() => {
        card.style.transition = 'opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), ' +
                                'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)'
        card.style.opacity = '1'
        card.style.transform = 'translateY(0)'
        setTimeout(() => {
          card.style.removeProperty('transition')
          card.style.removeProperty('opacity')
          card.style.removeProperty('transform')
        }, 350)
      }, delay)
    })

    // Step 6 – Badge pop
    this._animateBadge()
  }

  // Use padding+negative-margin to keep shadow bleed visible while
  // overflow-y is clipped for height transitions.
  _applyZoneOverflow(zone) {
    zone.style.overflowY = 'hidden'
    zone.style.paddingLeft = '8px'
    zone.style.paddingRight = '8px'
    zone.style.marginLeft = '-8px'
    zone.style.marginRight = '-8px'
  }

  _cleanUpZoneStyles(zone) {
    zone.style.removeProperty('transition')
    zone.style.removeProperty('height')
    zone.style.removeProperty('overflow')
    zone.style.removeProperty('overflow-y')
    zone.style.removeProperty('padding-left')
    zone.style.removeProperty('padding-right')
    zone.style.removeProperty('margin-left')
    zone.style.removeProperty('margin-right')
    zone.style.removeProperty('opacity')
  }

  _prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  // Coalesce rapid-fire zone refresh calls (e.g. health-system select-all)
  // into a single fetch per microtask checkpoint.
  _debouncedRefreshSelectedZone() {
    if (this._zoneRefreshPending) return
    this._zoneRefreshPending = true
    queueMicrotask(() => {
      this._zoneRefreshPending = false
      this._refreshSelectedZone()
    })
  }

  // Listen for a specific transitionend property, with a setTimeout fallback.
  _onTransitionEnd(el, property, fallbackMs, callback) {
    let done = false
    const finish = () => {
      if (done) return
      done = true
      el.removeEventListener('transitionend', onEnd)
      clearTimeout(timer)
      callback()
    }
    const onEnd = (e) => { if (e.propertyName === property) finish() }
    el.addEventListener('transitionend', onEnd)
    const timer = setTimeout(finish, fallbackMs)
  }

  // Remove all inline animation styles from a card.
  _resetCardStyles(card) {
    ;['transition', 'opacity', 'max-height', 'overflow',
      'margin-top', 'margin-bottom', 'padding-top', 'padding-bottom',
      'transform'].forEach(p => card.style.removeProperty(p))
  }

  // Animate a card out of the selected zone before removing it.
  _animateZoneCardOut(type, id, callback) {
    const row = document.querySelector(
      `#add_provider_selected_zone [data-add-provider-result-type="${type}"][data-add-provider-result-id="${id}"]`
    )
    const card = row?.closest('.border.rounded-xl')
    this._animateElementOut(card, callback)
  }

  // Animate any element out: opacity → height collapse.
  // The element is NOT removed from the DOM here — the Turbo Stream
  // replacement handles cleanup, preventing layout jump between removal
  // and re-render.
  _animateElementOut(el, callback) {
    if (!el || this._prefersReducedMotion()) {
      callback()
      return
    }

    const h = el.scrollHeight
    el.style.overflow = 'hidden'
    el.style.maxHeight = h + 'px'
    el.style.transition = 'none'
    void el.offsetHeight

    el.style.transition = 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), ' +
                          'max-height 250ms cubic-bezier(0.4, 0, 0.2, 1) 80ms, ' +
                          'margin-bottom 250ms cubic-bezier(0.4, 0, 0.2, 1) 80ms'
    el.style.opacity = '0'
    el.style.maxHeight = '0px'
    el.style.marginBottom = '0px'

    this._onTransitionEnd(el, 'max-height', 400, () => {
      callback()
    })
  }

  // Disable all form-bearing inputs inside an element so they cannot be
  // submitted during an exit animation.
  _disableFormInputs(el) {
    if (!el) return
    el.querySelectorAll('input, select, textarea').forEach(input => {
      input.disabled = true
    })
  }

  _enableFormInputs(el) {
    if (!el) return
    el.querySelectorAll('input:disabled, select:disabled, textarea:disabled').forEach(input => {
      if (input.closest('[data-tracked-disabled]')) return
      input.disabled = false
    })
  }

  _animateBadge() {
    const badge = document.querySelector('#add_provider_selected_zone .rounded-full.bg-core-purple-500')
    if (!badge || this._prefersReducedMotion()) return
    badge.style.transition = 'none'
    badge.style.transform = 'scale(0.5)'
    badge.style.opacity = '0'
    requestAnimationFrame(() => {
      badge.style.transition = 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
                               'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)'
      badge.style.transform = 'scale(1)'
      badge.style.opacity = '1'
    })
    setTimeout(() => {
      badge.style.removeProperty('transition')
      badge.style.removeProperty('transform')
      badge.style.removeProperty('opacity')
    }, 350)
  }

  // After new search results load, immediately hide cards that are already
  // selected (they live in the selected zone instead).
  _hideSelectedSearchResults() {
    this.selections.forEach(({ type, id }) => {
      const row = this._findSearchResultRow(type, id)
      if (!row) return
      const card = row.closest('.border.rounded-xl')
      if (card) {
        card.classList.add('hidden')
        this._disableFormInputs(card)
      }
    })
  }

  // Re-show all hidden search result cards (used by clearSelections).
  _showAllSearchResults() {
    const results = document.getElementById('add_provider_search_results')
    if (!results) return
    results.querySelectorAll('.border.rounded-xl.hidden').forEach((card) => {
      card.classList.remove('hidden')
      this._resetCardStyles(card)
      this._enableFormInputs(card)
    })
  }

  // Animate a search-result card back in when deselected.
  _fadeInSearchResult(type, id) {
    const row = this._findSearchResultRow(type, id)
    if (!row) return
    const card = row.closest('.border.rounded-xl')
    if (!card) return

    this._enableFormInputs(card)

    if (this._prefersReducedMotion()) {
      card.classList.remove('hidden')
      this._resetCardStyles(card)
      return
    }

    // Measure target height with styles cleared
    card.classList.remove('hidden')
    this._resetCardStyles(card)
    const targetH = card.scrollHeight

    // Snap to collapsed state
    card.style.transition = 'none'
    card.style.opacity = '0'
    card.style.maxHeight = '0px'
    card.style.overflow = 'hidden'
    card.style.marginBottom = '0px'
    void card.offsetHeight // force reflow

    // Animate to expanded
    card.style.transition = 'max-height 280ms cubic-bezier(0.4, 0, 0.2, 1), ' +
                            'margin-bottom 280ms cubic-bezier(0.4, 0, 0.2, 1), ' +
                            'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 60ms'
    card.style.maxHeight = targetH + 20 + 'px'
    card.style.removeProperty('margin-bottom')
    card.style.opacity = '1'

    this._onTransitionEnd(card, 'max-height', 400, () => this._resetCardStyles(card))
  }

  _toggleSettingsPanel(resultRow, show) {
    const panel = resultRow.querySelector("[data-add-provider-counter-settings-panel]")
    if (!panel) return
    panel.classList.toggle("hidden", !show)
  }

  _updateSummary() {
    const n = this.selections.size

    const summary = document.getElementById("add_provider_counter_summary")
    if (summary) {
      summary.textContent =
        n === 0
          ? "No entities selected"
          : `${n} ${n === 1 ? "entity" : "entities"} selected`
    }

    const submit = document.getElementById("add_provider_submit")
    if (submit) submit.disabled = n === 0
  }
}
